// React import usunięty (nowy JSX transform)
import { useNavigate, Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useGame } from '../context/GameContext';
import MobilePageShell from '../components/MobilePageShell';
import { useI18n } from '../i18n/I18nProvider';
import { fullResetPersistentState, readSession, writeSession } from '../utils/sessionContract';
import { isNativeApp } from '../utils/appLinks';
import { ScreenCard } from '../layout/regions/ScreenCard';
import { ScreenHeader } from '../layout/regions/ScreenHeader';
import { useLayout } from '../layout/useLayout';
import { getScreenLayout, isSplitStartLayout } from '../layout/layouts';
import { isDevEnv } from '../utils/debugFlags';
import { loadGroupToken } from '../utils/groupPersistence';
import { resolveOnlineAutoResumePath } from '../utils/onlineResume';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

interface HomeSecondaryActionItem {
  id: 'guide' | 'stats' | 'account' | 'scores' | 'scenarios';
  shortLabel: string;
  emoji: string;
  onClick: () => void;
}

export default function Home() {
  const navigate = useNavigate();
  const { resetGame, exitGame } = useGame();
  const resumeActionTakenRef = useRef(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installAvailable, setInstallAvailable] = useState(false);
  const [isIos] = useState(() => /iPhone|iPad|iPod/i.test(window.navigator.userAgent));
  const [isSafari] = useState(() => (
    /iPhone|iPad|iPod/i.test(window.navigator.userAgent)
    && /Safari/i.test(window.navigator.userAgent)
    && !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|GSA|FBAN|FBAV|Instagram/i.test(window.navigator.userAgent)
  ));
  const [isStandalone] = useState(() => {
    const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean };
    return Boolean(
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
      || navigatorWithStandalone.standalone
    );
  });
  const [showIosHelp, setShowIosHelp] = useState(false);
  const { t } = useI18n();
  const { layoutId } = useLayout();
  const isNative = isNativeApp();
  const [pendingOnlineResumePath, setPendingOnlineResumePath] = useState<string | null>(() => (
    isNative ? resolveOnlineAutoResumePath(readSession(), loadGroupToken()) : null
  ));
  const showDevScores = isDevEnv() || import.meta.env.MODE === 'test';
  const screenLayout = getScreenLayout(layoutId, 'home');
  const isSplitHero = isSplitStartLayout(screenLayout);
  const allowHomeScroll = !isSplitHero && showIosHelp;
  const secondaryActions: HomeSecondaryActionItem[] = [
    {
      id: 'guide',
      shortLabel: t('home.guideHint'),
      emoji: '📘',
      onClick: () => navigate('/guide'),
    },
    {
      id: 'stats',
      shortLabel: t('home.statsHint'),
      emoji: '🎲',
      onClick: () => navigate('/stats'),
    },
    {
      id: 'account',
      shortLabel: t('home.accountHint'),
      emoji: '👤',
      onClick: () => navigate('/account'),
    },
  ];

  if (showDevScores) {
    secondaryActions.push({
      id: 'scores',
      shortLabel: t('home.scoresHint'),
      emoji: '🏁',
      onClick: () => navigate('/scores'),
    });
    secondaryActions.push({
      id: 'scenarios',
      shortLabel: t('home.scenariosHint'),
      emoji: '🧪',
      onClick: () => navigate('/scenarios'),
    });
  }

  // Na ekranie startowym zawsze czyścimy stan gry – zostają tylko preferencje (motyw/język).
  // Wyjątek: natywny prompt wznowienia aktywnej sesji online.
  useEffect(() => {
    if (pendingOnlineResumePath) return;
    if (resumeActionTakenRef.current) return;
    exitGame();
  }, [exitGame, pendingOnlineResumePath]);

  useEffect(() => {
    const handler = (e: Event) => {
      const promptEvent = e as BeforeInstallPromptEvent;
      e.preventDefault();
      setDeferredPrompt(promptEvent);
      setInstallAvailable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallPWA = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice; // outcome opcjonalny
    setDeferredPrompt(null);
    setInstallAvailable(false);
  };

  const handleResumeOnlineGame = () => {
    if (!pendingOnlineResumePath) return;
    resumeActionTakenRef.current = true;
    navigate(pendingOnlineResumePath, { replace: true, state: { promptedResume: true } });
  };

  const handleDismissOnlineResume = () => {
    resumeActionTakenRef.current = false;
    setPendingOnlineResumePath(null);
  };

  // Tworzenie nowej gry (tryb w realu) z ekranu startowego -> przejście do lobby real
  const handleCreateRealGame = () => {
    try { fullResetPersistentState(); } catch {}
    resetGame();
    try {
      writeSession({
        version: 1,
        mode: 'REAL',
        gameId: '',
        role: 'host',
        phase: 'lobby',
        lastPath: '/real',
        updatedAt: new Date().toISOString(),
      });
    } catch {}
    navigate('/real');
  };

  const legalLinks = (
    <>
      <Link to="/privacy" className="home-legal-link text-brand-text/40 hover:text-brand-text/70 transition-colors">
        {t('privacy.title')}
      </Link>
      <Link to="/terms" className="home-legal-link text-brand-text/40 hover:text-brand-text/70 transition-colors">
        {t('terms.title')}
      </Link>
    </>
  );

  return (
    <MobilePageShell
      screenKey="home"
      hideHeader
      centered={isSplitHero}
      scroll={allowHomeScroll}
      className={isSplitHero ? '' : 'home-screen'}
    >
      <h1 className="sr-only">{t('home.title')}</h1>
      <div className={isSplitHero ? '' : 'home-screen__viewport'}>
        <ScreenCard className={`home-hub w-full animate-fade-in ${isSplitHero ? 'mt-3 p-4 sm:mt-4 sm:p-8' : 'home-screen__card'}`.trim()}>
          <div
            className={`home-hub__layout ${isSplitHero ? 'grid gap-8 lg:grid-cols-[minmax(24rem,0.94fr)_minmax(30rem,1.06fr)] lg:items-center' : ''}`.trim()}
            data-layout-template={screenLayout.template}
          >
            <div className={`home-hub__brand ${isSplitHero ? 'lg:pr-4' : ''}`.trim()}>
              <div className="home-hub__brand-panel">
                <ScreenHeader
                  title={t('home.title')}
                  media={(
                    <img
                      src="/img/dice1k.png"
                      alt={t('home.title')}
                      className="home-hub__brand-art mx-auto object-contain drop-shadow-md"
                    />
                  )}
                  align="center"
                  className="home-hub__brand-header"
                  titleClassName="home-title home-title--outline home-title--mobile"
                />
              </div>
            </div>

            <div className="home-hub__actions">
              <div className={`home-hub__actions-panel ${isSplitHero ? 'space-y-4' : ''}`.trim()}>
                <>
                  {pendingOnlineResumePath && (
                    <ScreenCard surface="soft" className="mb-4 rounded-2xl p-4 text-left shadow-none">
                      <p className="text-sm font-semibold text-accent">{t('home.resumeOnlineTitle')}</p>
                      <p className="mt-2 text-sm text-brand-text/75">{t('home.resumeOnlineHint')}</p>
                      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <button
                          onClick={handleResumeOnlineGame}
                          className="w-full rounded-xl bg-(--color-accent) px-4 py-3 text-sm font-semibold text-brand-dark transition-opacity hover:opacity-90"
                          data-testid="home-resume-online-btn"
                        >
                          {t('home.resumeOnlineAction')}
                        </button>
                        <button
                          onClick={handleDismissOnlineResume}
                          className="w-full rounded-xl border border-brand-border px-4 py-3 text-sm font-semibold text-brand-text transition-colors hover:bg-brand-border/10"
                          data-testid="home-dismiss-online-resume-btn"
                        >
                          {t('home.resumeOnlineDismiss')}
                        </button>
                      </div>
                    </ScreenCard>
                  )}

                  <div className="home-hub__primary-actions">
                    <button
                      onClick={() => navigate('/online')}
                      className="home-hub__cta home-hub__cta--primary home-hub__cta--major w-full btn-cta-online"
                      data-testid="create-online-game-btn"
                    >
                      {t('home.newGameShort')}
                      <span className="sr-only">Online Lobby</span>
                    </button>
                    <button
                      onClick={handleCreateRealGame}
                      className="home-hub__cta home-hub__cta--primary home-hub__cta--major w-full btn-cta-score"
                      data-testid="create-real-game-btn"
                    >
                      {t('home.recordScores')}
                      <span className="sr-only">Tryb w realu</span>
                      <span className="hidden" data-testid="create-new-game-alias">Create New Game</span>
                    </button>
                  </div>

                  <div className="home-hub__secondary-grid grid grid-cols-3">
                    {secondaryActions.slice(0, 3).map((action) => (
                      <button
                        key={action.id}
                        onClick={action.onClick}
                        className="w-full home-btn-secondary home-hub__secondary-action home-hub__secondary-action--compact"
                      >
                        <span className="home-hub__secondary-emoji" aria-hidden="true">{action.emoji}</span>
                        <span className="home-hub__secondary-title">{action.shortLabel}</span>
                      </button>
                    ))}
                  </div>
                  {secondaryActions.slice(3).map((action) => (
                    <button
                      key={action.id}
                      onClick={action.onClick}
                      className="w-full home-btn-secondary home-hub__secondary-action home-hub__secondary-action--wide"
                    >
                      <span className="home-hub__secondary-emoji" aria-hidden="true">{action.emoji}</span>
                      <span className="home-hub__secondary-title">{action.shortLabel}</span>
                    </button>
                  ))}
                  {installAvailable && !isStandalone && !isNative && (
                    <button
                      onClick={handleInstallPWA}
                      className="home-hub__install-button w-full rounded-lg bg-linear-to-r from-accent to-accent-strong text-brand font-semibold text-lg shadow-sm hover:opacity-90 transition"
                    >
                      {t('home.install')}
                    </button>
                  )}
                </>

                {!installAvailable && isIos && isSafari && !isStandalone && !isNative && (
                  <div className="mt-4 space-y-2">
                    <button
                      onClick={() => setShowIosHelp((v) => !v)}
                      className="w-full py-3 rounded-lg bg-linear-to-r from-[#8ecae6] to-brand-primary text-brand-dark font-semibold text-sm shadow-sm hover:opacity-90 transition"
                    >
                      {t('home.iosHow')}
                    </button>
                    {showIosHelp && (
                      <ScreenCard surface="soft" className="p-4 text-xs leading-relaxed space-y-2 shadow-none">
                        <p className="font-semibold text-accent">{t('home.ios.title')}</p>
                        <ol className="list-decimal list-inside space-y-1 text-[#e0f2f8]">
                          <li>{t('home.ios.step1')}</li>
                          <li>
                            {t('home.ios.step2a')}{' '}
                            <span className="inline-block px-1 py-0.5 bg-brand-dark rounded-sm border border-brand-border">
                              {t('home.ios.share')}
                            </span>{' '}
                            {t('home.ios.step2b')}
                          </li>
                          <li>{t('home.ios.step3')}</li>
                          <li>{t('home.ios.step4')}</li>
                        </ol>
                        <p className="opacity-70">
                          {t('home.ios.after')}
                        </p>
                      </ScreenCard>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </ScreenCard>

        {isSplitHero ? (
          <div className="home-legal-links fixed left-1/2 z-30 -translate-x-1/2 px-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center">
            {legalLinks}
          </div>
        ) : (
          <div className="home-screen__legal-lane">
            <div className="home-legal-links home-screen__legal-links px-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center">
              {legalLinks}
            </div>
          </div>
        )}
      </div>
    </MobilePageShell>
  );
}
